import { useCallback, useEffect, useState, MutableRefObject } from 'react';
import { getVideoLayout } from 'utils/video-layout-helper';
import { Dimension, Pagination, CellLayout } from 'shared/types/video.interface';
import { ZoomClient, MediaStream, Participants } from 'index-types';
import { isShallowEqual } from 'utils/util';
import { useRenderVideo } from './useRenderVideo';
import { useParticipantsChange } from './useParticipantsChange';

export function useGalleryLayout(
  zmClient: ZoomClient,
  mediaStream: MediaStream | null,
  isVideoDecodeReady: boolean,
  videoRef: MutableRefObject<HTMLCanvasElement | null>,
  dimension: Dimension,
  pagination: Pagination,
) {
  const [visibleParticipants, setVisibleParticipants] = useState<Participants[]>([]);
  const [participantsLayout, setParticipantsLayout] = useState<
    { user: Participants; layout: CellLayout }[]
  >([]);
  const [subscribedVideos, setSubscribedVideos] = useState<number[]>([]);

  const { page, pageSize, totalPage, totalSize } = pagination;

  let size = pageSize;
  if (page === totalPage - 1) {
    size = Math.min(size, totalSize % pageSize || size);
  }

  const createParticipantsLayout = useCallback(
    (participants: Participants[]) => {
      const layouts = getVideoLayout(dimension.width, dimension.height, size);
      return participants
        .map((user, i) => {
          const layout = layouts[i];
          // Only include participants that have a valid layout
          if (layout) {
            return {
              user,
              layout,
            };
          }
          return null;
        })
        .filter(Boolean) as { user: Participants; layout: CellLayout }[];
    },
    [dimension.width, dimension.height, size],
  );

  const onParticipantsChange = useCallback(
    (participants: Participants[]) => {
      const currentUser = zmClient.getCurrentUserInfo();
      if (currentUser && participants.length > 0) {
        let pageParticipants: Participants[] = [];
        if (participants.length === 1) {
          pageParticipants = participants;
        } else {
          pageParticipants = participants
            .filter((user) => user.userId !== currentUser.userId)
            .sort((user1, user2) => Number(user2.bVideoOn) - Number(user1.bVideoOn));
          pageParticipants.splice(1, 0, currentUser);
          pageParticipants = pageParticipants.filter(
            (_user, index) => Math.floor(index / pageSize) === page,
          );
        }
        setVisibleParticipants(pageParticipants);

        // Use fresh layout calculation for participantsLayout (for video rendering)
        const newLayouts = createParticipantsLayout(pageParticipants);
        setParticipantsLayout(newLayouts);

        const videoParticipants = pageParticipants
          .filter((user) => user.bVideoOn)
          .map((user) => user.userId);
        setSubscribedVideos(videoParticipants);
      }
    },
    [createParticipantsLayout, page, pageSize, zmClient],
  );

  useParticipantsChange(zmClient, onParticipantsChange);

  useRenderVideo(
    mediaStream,
    isVideoDecodeReady,
    videoRef,
    subscribedVideos,
    participantsLayout, // For video rendering
    zmClient.getCurrentUserInfo()?.userId,
  );

  useEffect(() => {
    if (visibleParticipants.length > 0 && dimension.width > 0 && dimension.height > 0) {
      const newLayouts = createParticipantsLayout(visibleParticipants);

      // Only update if layout actually changed
      if (!isShallowEqual(participantsLayout[0]?.layout, newLayouts[0]?.layout)) {
        setParticipantsLayout(newLayouts);
      }
    }
  }, [
    createParticipantsLayout,
    visibleParticipants,
    participantsLayout,
    dimension.width,
    dimension.height,
  ]);

  return {
    participantsLayout,
  };
}
